import '../faust.config';
import React, {useEffect, useMemo} from 'react';
import {useRouter} from 'next/router';
import {FaustProvider} from '@faustwp/core';
import {ThemeProvider} from 'next-themes';
// import '@faustwp/core/dist/css/toolbar.css'; // Moved to simplified toolbar.scss
import dynamic from "next/dynamic";
import Script from 'next/script';
import { AuthProvider } from '@/components/Auth/AuthProvider';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';

import '@/lib/assets/styles/global.scss';
import {InitializeGPT} from "@/components/GPT/GPT";
import {PageLevelAdTargetContext} from '@/lib/utils/context';
import {sendIpsosEvent} from "@/lib/utils/ipsos";

const NextNProgress = dynamic(() => import('nextjs-progressbar'), {ssr: false});

// Import GPT with NoSSR to avoid hydration issues
const Snippets = dynamic(() => import('@/components/Snippets/Snippets').then((mod) => mod.default), {ssr: false});
const Utils = dynamic(() => import('@/components/Utils/Utils').then((mod) => mod.default), {ssr: false});
const Toaster = dynamic(() => import('@/lib/shadcn/ui/sonner').then((mod) => mod.Toaster), {ssr: false});
const UserLoginDialog = dynamic(() => import('@/components/Auth/UserLoginDialog').then((mod) => mod.default), {ssr: false});
const GoogleAutoSignIn = dynamic(() => import('@/components/Auth/GoogleAutoSignIn').then((mod) => mod.default), {ssr: false});
const UserOnboarding = dynamic(() => import('@/components/Auth/UserOnboarding').then((mod) => mod.default), {ssr: false});
const Lightbox = dynamic(() => import('@/components/Lightbox/Lightbox').then((mod) => mod.default), {ssr: false});

export default function MyApp({Component, pageProps}) {
  const router = useRouter();

  // Page level ad targeting
  // All objects should have momAdTargeting(objectDatabaseID) in their gql for this to work
  const momAdTargeting = pageProps?.__TEMPLATE_QUERY_DATA__?.momAdTargeting;
  const pageLevelTargeting = useMemo(() => JSON.parse(momAdTargeting ?? '{}') ?? {}, [momAdTargeting]);

  useEffect(() => {
    // Load page level ad targeting
    if (typeof window !== 'undefined' && Object.keys(pageLevelTargeting).length) {
      window.googletag = window.googletag || {cmd: []};

      window.googletag.cmd.push(function () {
        Object.keys(pageLevelTargeting).forEach(function (key) {
          window.googletag.pubads().setTargeting(key, pageLevelTargeting[key]);
        });
      });
    }

    // IPSOS on template change
    const {section} = pageLevelTargeting || {};
    sendIpsosEvent(section);
  }, [momAdTargeting]);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider defaultTheme={'system'} disableTransitionOnChange attribute="class">
          <FaustProvider pageProps={pageProps}>
            <Script
              src="https://accounts.google.com/gsi/client"
              strategy="lazyOnload"
            />
            <NextNProgress height={3} color={'#3D6D8C'} options={{showSpinner: false}}/>
            <Utils/>

            <PageLevelAdTargetContext.Provider value={pageLevelTargeting}>
              <InitializeGPT/>

              <Component {...pageProps} key={router.asPath}/>

              <Snippets/>
            </PageLevelAdTargetContext.Provider>

            <UserLoginDialog />
            <UserOnboarding />
            <GoogleAutoSignIn />
            <Lightbox />

            <Toaster position="top-right" />
          </FaustProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
